import { Controller } from "@hotwired/stimulus"

// Live countdown to a deadline, as one HH:MM:SS "clock" target or one target
// per unit. At zero the clock freezes and, when the markup has them, the
// "running" element gives way to the "overdue" one without a reload. Hours are
// not capped at 24: a deadline counted in business hours can sit several days away.
//
// With a "progress" target and a "startedAt" value, the bar shrinks from the
// start of the delay to the deadline, in wall-clock time.
export default class extends Controller {
  static targets = ["clock", "hours", "minutes", "seconds", "running", "overdue", "progress"]
  static values = { deadline: String, startedAt: String }

  connect() {
    this.deadline = new Date(this.deadlineValue)
    this.startedAt = this.hasStartedAtValue ? new Date(this.startedAtValue) : null
    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    clearInterval(this.timer)
  }

  tick() {
    const remaining = Math.floor((this.deadline - Date.now()) / 1000)
    if (remaining <= 0) {
      this.expire()
      return
    }
    this.render(remaining)
    this.updateProgress(remaining)
  }

  expire() {
    clearInterval(this.timer)
    this.render(0)
    this.updateProgress(0)
    if (!this.hasOverdueTarget) return

    this.runningTarget.hidden = true
    this.overdueTarget.hidden = false
  }

  render(remaining) {
    if (this.hasClockTarget) {
      this.renderClock(remaining)
    } else {
      this.renderUnits(remaining)
    }
  }

  renderClock(remaining) {
    this.clockTarget.textContent = this.units(remaining).join(":")
  }

  // One target per unit lets the markup own the separators: the order
  // confirmation page greys out the colons, as designed.
  renderUnits(remaining) {
    const [hours, minutes, seconds] = this.units(remaining)
    this.hoursTarget.textContent = hours
    this.minutesTarget.textContent = minutes
    this.secondsTarget.textContent = seconds
  }

  updateProgress(remaining) {
    if (!this.hasProgressTarget || !this.startedAt) return

    const total = Math.max((this.deadline - this.startedAt) / 1000, 1)
    const ratio = Math.min(Math.max(remaining / total, 0), 1)
    this.progressTarget.style.width = `${(ratio * 100).toFixed(1)}%`
  }

  units(seconds) {
    const parts = [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    return parts.map((part) => String(part).padStart(2, "0"))
  }
}
